'use client'

import { ChainIcon } from '@/components/svg/chain-icon/chain-icon'
import { InstagramIcon } from '@/components/svg/instagram-icon/instagram-icon'
import { ButtonV4 } from '@/components/v4/atoms/button/button'
import { GTM_EVENT } from '@/constants'
import { sendGTMEvent } from '@next/third-parties/google'
import type { MouseEventHandler } from 'react'
import s from './icon-link-button.module.scss'

type TrackingType = 'website' | 'instagram'

type Props = {
  href: string
  trackingType: TrackingType
  supplierPath: string
}

function assertUnreachable(x: never): never {
  throw new Error(`Unhandled tracking type: ${String(x)}`)
}

function sendTrackingEvent(trackingType: TrackingType, supplierPath: string, linkUrl: string) {
  switch (trackingType) {
    case 'instagram':
      sendGTMEvent({
        event: GTM_EVENT.SUPPLIER_CARD_INSTAGRAM_CLICK,
        'supplier_profile_path': supplierPath,
        'link_url': linkUrl,
        'link_location': 'thumbnail_card',
      })
      return
    case 'website':
      sendGTMEvent({
        event: GTM_EVENT.SUPPLIER_CARD_WEBSITE_CLICK,
        'supplier_profile_path': supplierPath,
        'link_url': linkUrl,
        'link_location': 'thumbnail_card',
      })
      return
    default:
      assertUnreachable(trackingType)
  }
}

function renderIcon(trackingType: TrackingType) {
  switch (trackingType) {
    case 'website':
      return <ChainIcon />
    case 'instagram':
      return <InstagramIcon />
    default:
      assertUnreachable(trackingType)
  }
}

export function IconLinkButton({ href, trackingType, supplierPath }: Props) {
  const handleClick: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation()
    sendTrackingEvent(trackingType, supplierPath, href)
  }

  return (
    <span onClick={handleClick} role="presentation">
      <ButtonV4
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="light"
        className={s.button}
      >
        {renderIcon(trackingType)}
      </ButtonV4>
    </span>
  )
}
